/**
 * Popover that adds a hidden column to the Home doc-list, opened by `DocListTable`
 * from "Add column" and from a column's "Insert column". Options group into a
 * cascader by kind; typing switches to a flat filtered list.
 *
 * In card mode the cascader expands on click, because touch has no hover.
 */
import { FC, useMemo, useState } from 'react';
import { Button, Cascader, Input, Popover, Typography, Empty, theme } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { DefaultOptionType } from 'antd/es/select';

import FlexCol from '../../../../atoms/FlexCol';
import {
  DOCLIST_META_COLUMN_IDS,
  DOCLIST_FORM_PROPERTY_COLUMN_IDS,
} from '#types/System.Parameters.types';

const { Text } = Typography;

export interface ColumnPickerOption {
  id: string;
  label: string;
}

interface ColumnPickerProps {
  open: boolean;
  position: { x: number; y: number };
  options: ColumnPickerOption[];
  title: string;
  onPick: (columnId: string) => void;
  onClose: () => void;
  cardMode?: boolean;
}

const ColumnPicker: FC<ColumnPickerProps> = ({
  open,
  position,
  options,
  title,
  onPick,
  onClose,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const [search, setSearch] = useState('');

  const available = useMemo(() => {
    return options.filter((o) => {
      if (o.id === 'actions') return false;
      if (o.id === 'attachments') return false;
      if (o.id.startsWith('table_')) return false;

      return true;
    });
  }, [options]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter((o) => o.label.toLowerCase().includes(q) || o.id.toLowerCase().includes(q));
  }, [available, search]);

  const availableIdSet = useMemo(() => new Set(available.map((o) => o.id)), [available]);

  const cascaderOptions = useMemo(() => {
    const metaColumnIds = DOCLIST_META_COLUMN_IDS;
    const formPropertyColumnIds = DOCLIST_FORM_PROPERTY_COLUMN_IDS;

    const fieldOptions: DefaultOptionType[] = [];
    const entityOptions: DefaultOptionType[] = [];
    const metaOptions: DefaultOptionType[] = [];
    const formOptions: DefaultOptionType[] = [];

    const groupByEntityId = new Map<string, DefaultOptionType[]>();

    for (const o of available) {
      if (o.id.startsWith('field_')) {
        fieldOptions.push({ label: o.label, value: o.id });
        continue;
      }

      if (o.id.startsWith('entity_') || o.id === 'project') {
        entityOptions.push({ label: o.label, value: o.id });
        continue;
      }

      if (o.id.startsWith('group_')) {
        const rest = o.id.slice('group_'.length);
        const firstUnderscoreIdx = rest.indexOf('_');
        const entityId = firstUnderscoreIdx >= 0 ? rest.slice(0, firstUnderscoreIdx) : rest;
        const list = groupByEntityId.get(entityId) ?? [];
        list.push({ label: o.label, value: o.id });
        groupByEntityId.set(entityId, list);
        continue;
      }

      if (metaColumnIds.has(o.id)) {
        metaOptions.push({ label: o.label, value: o.id });
        continue;
      }

      if (formPropertyColumnIds.has(o.id)) {
        formOptions.push({ label: o.label, value: o.id });
        continue;
      }

      formOptions.push({ label: o.label, value: o.id });
    }

    fieldOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    entityOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    metaOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)));
    formOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)));

    const recordGroupChildren = Array.from(groupByEntityId.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([entityId, children]) => ({
        label: entityId,
        value: `record-groups-${entityId}`,
        children: children.sort((a, b) => String(a.label).localeCompare(String(b.label))),
      }));

    const next: DefaultOptionType[] = [];

    if (fieldOptions.length > 0) {
      next.push({ label: 'Fields', value: 'fields', children: fieldOptions });
    }
    if (entityOptions.length > 0) {
      next.push({ label: 'Records', value: 'records', children: entityOptions });
    }
    if (recordGroupChildren.length > 0) {
      next.push({ label: 'Record Groups', value: 'record-groups', children: recordGroupChildren });
    }
    if (metaOptions.length > 0) {
      next.push({ label: 'Meta Fields', value: 'meta-fields', children: metaOptions });
    }
    if (formOptions.length > 0) {
      next.push({ label: 'Form Properties', value: 'form-properties', children: formOptions });
    }

    return next;
  }, [available]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setSearch('');
          onClose();
        }
      }}
      trigger="click"
      placement="bottomLeft"
      content={
        <FlexCol gap={8} style={{ width: 680, maxWidth: 'calc(100vw - 40px)' }}>
          <FlexCol noGap>
            <Text type="secondary">{title}</Text>
          </FlexCol>
          <Input
            aria-label="Column picker search"
            placeholder="Search columns…"
            prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            autoFocus
          />
          {search.trim().length > 0 ? (
            filtered.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching columns" />
            ) : (
              <div style={{ maxHeight: 320, overflow: 'auto' }}>
                {filtered.map((o) => (
                  <Button
                    key={o.id}
                    aria-label={`Pick column ${o.label}`}
                    type="text"
                    block
                    onClick={() => {
                      onPick(o.id);
                      setSearch('');
                      onClose();
                    }}
                    style={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      padding: `${token.paddingXS}px ${token.paddingSM}px`,
                      height: 'auto',
                      whiteSpace: 'normal',
                    }}
                  >
                    <Text>{o.label}</Text>
                  </Button>
                ))}
              </div>
            )
          ) : cascaderOptions.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No matching columns" />
          ) : (
            <div style={{ maxWidth: '100%', maxHeight: 340, overflow: 'auto' }}>
              <Cascader.Panel
                options={cascaderOptions}
                expandTrigger={cardMode ? 'click' : 'hover'}
                onChange={(value) => {
                  const picked = value[value.length - 1];
                  if (typeof picked === 'string' && availableIdSet.has(picked)) {
                    onPick(picked);
                    onClose();
                  }
                }}
              />
            </div>
          )}
        </FlexCol>
      }
    >
      <div
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: 1,
          height: 1,
        }}
      />
    </Popover>
  );
};

export default ColumnPicker;

